import type { Commit } from '@/pages/developer/types';

export const extractPullRequestNumberFromCommitMessage = (message: string): number | null => {
  const trimmed = message.trim();
  if (!trimmed) return null;

  // GitHub merge commits.
  const mergeMatch = trimmed.match(/\bmerge\s+pull\s+request\s+#(\d+)\b/i);
  if (mergeMatch?.[1]) return Number(mergeMatch[1]);

  // Squash-merge style: "Some change (#123)".
  const parenMatch = trimmed.match(/\(#(\d+)\)\s*$/);
  if (parenMatch?.[1]) return Number(parenMatch[1]);

  // Other common patterns.
  const hashMatch = trimmed.match(/\bPR\s*#(\d+)\b/i);
  if (hashMatch?.[1]) return Number(hashMatch[1]);

  return null;
};

export const isPullRequestCommit = (commit: Pick<Commit, 'message'>): boolean => {
  return extractPullRequestNumberFromCommitMessage(commit.message) !== null;
};

export const parseGitHubRepoIdentity = (ownerInput: string, repoInput: string) => {
  const ownerRaw = ownerInput.trim();
  const repoRaw = repoInput.trim();

  const tryParseFromUrl = (value: string) => {
    const match = value.match(/github\.com\/([^/]+)\/([^/?#]+)(?:\.git)?/i);
    if (!match) return null;
    return { owner: match[1], repo: match[2].replace(/\.git$/i, '') };
  };

  const urlCandidate = ownerRaw.startsWith('http') ? ownerRaw : repoRaw.startsWith('http') ? repoRaw : null;
  if (urlCandidate) {
    const parsed = tryParseFromUrl(urlCandidate);
    if (parsed) return parsed;
  }

  if (ownerRaw.includes('/') && !repoRaw) {
    const [owner, repo] = ownerRaw.split('/').map((part) => part.trim());
    if (owner && repo) return { owner, repo };
  }

  return { owner: ownerRaw, repo: repoRaw };
};

export const encodeGitHubPath = (value: string) => encodeURIComponent(value.trim());

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const mapWithConcurrency = async <T, R>(
  items: T[],
  worker: (item: T) => Promise<R>,
  options: { concurrency: number; pauseMs?: number }
) => {
  const concurrency = Math.max(1, options.concurrency);
  const pauseMs = options.pauseMs ?? 0;
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const runWorker = async () => {
    while (true) {
      const index = nextIndex;
      nextIndex += 1;
      if (index >= items.length) return;
      results[index] = await worker(items[index]);
      if (pauseMs > 0) {
        await sleep(pauseMs);
      }
    }
  };

  await Promise.all(new Array(concurrency).fill(0).map(() => runWorker()));
  return results;
};

export function normalizeBillingAmount(value: unknown, fallbackRate: number) {
  const parsed = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : fallbackRate;
  const finite = Number.isFinite(parsed) ? parsed : fallbackRate;
  const nonNegative = finite < 0 ? fallbackRate : finite;
  const MAX_REASONABLE_RATE = 10000; // Guard against corrupted Firestore values
  return Math.min(nonNegative, MAX_REASONABLE_RATE);
}

export const sanitizeCommitForFirestore = (commit: Commit, fallbackRate: number) => {
  const billingAmount = normalizeBillingAmount(commit.billingAmount, fallbackRate);

  const next: Commit = {
    sha: commit.sha,
    message: commit.message,
    author: commit.author,
    date: commit.date,
    url: commit.url,
    billed: commit.billed ?? false,
    billingAmount,
  };

  if (typeof commit.additions === 'number') {
    next.additions = commit.additions;
  }

  if (typeof commit.deletions === 'number') {
    next.deletions = commit.deletions;
  }

  if (typeof commit.filesChanged === 'number') {
    next.filesChanged = commit.filesChanged;
  }

  if (typeof commit.milestoneId === 'string') {
    next.milestoneId = commit.milestoneId;
  }

  if (typeof commit.milestoneTitle === 'string') {
    next.milestoneTitle = commit.milestoneTitle;
  }

  if (commit.workStatus === 'unassigned' || commit.workStatus === 'assigned' || commit.workStatus === 'completed') {
    next.workStatus = commit.workStatus;
  }

  if (typeof commit.workorderId === 'string') {
    next.workorderId = commit.workorderId;
  }

  return next;
};
